import { assign, fromPromise, sendTo, setup } from 'xstate';
import { showAxiosErrorAction } from '~/features/actions';
import { createEmployerFacility } from '~/features/market/facility';
import { facilityMachine } from '~/features/vault/facility/facility.machine';
import {
  createFacilityViewState,
  onFacilityFormOpen,
} from '~/features/vault/facility/subMachineHelper';
import {
  closeModal,
  openModal,
  removeFacilitiesFromContext,
  resetLoading,
  setEmployerFacility,
  setFacility,
  setListingId,
  setLoading,
  setMsaasJobListingFacilities,
  setSubmittedStepValues,
} from './actions';
import { jobListingSubmit, msaasSubmit } from './api';
import { Context, Events, OnAddFacilitiesEvent, OnSubmitEvent, Scope } from './types';

export const facilitiesMachineSetupCreator = (
  API_CALLS: typeof msaasSubmit | typeof jobListingSubmit
) =>
  setup({
    types: {
      context: {} as Context,
      events: {} as Events,
      input: {} as Partial<Context>,
    },
    actors: {
      facilityRef: facilityMachine,
      submit: fromPromise(API_CALLS),
      createEmployerFacility: fromPromise(createEmployerFacility),
    },
    actions: {
      setFacility: assign(setFacility),
      setEmployerFacility: assign(setEmployerFacility),
      sendError: showAxiosErrorAction,
      setLoading: assign(setLoading),
      resetLoading: assign(resetLoading),
      openModal: assign(openModal),
      closeModal: assign(closeModal),
      setListingId: assign(setListingId),
      setFacilities: assign(setMsaasJobListingFacilities),
      setSubmittedStepValues: assign(setSubmittedStepValues),
      removeFacilitiesFromContext: assign(removeFacilitiesFromContext),
      sendUpdatedFacilityStepToParent: sendTo(
        ({ context }) => {
          const parentRef = context.parentRef;
          if (!parentRef) {
            throw new Error('parentRef is required in facilitiesMachine');
          }
          return parentRef;
        },
        ({ context }: Scope) => ({
          type: 'ON_UPDATE_FACILITY_STEP',
          payload: {
            facilities: context.msaasJobListingFacilities,
            stateCodes: context.states,
            // [MCV-1523] Without this the parent never learns the primary
            // facility changed, so returning to this step re-hydrated from a
            // listing whose primaryEmployerFacilityId was still stale.
            primaryFacility: context.primaryFacility,
          },
        })
      ),
      sendToNextPage: sendTo(
        ({ context }) => {
          const parentRef = context.parentRef;
          if (!parentRef) {
            throw new Error('parentRef is required in facilitiesMachine');
          }
          return parentRef;
        },
        () => ({ type: 'ON_NEXT' })
      ),
    },
  });

export const msaasFacilitiesMachineSetup = facilitiesMachineSetupCreator(msaasSubmit);

export const jobListingFacilitiesMachineSetup = facilitiesMachineSetupCreator(jobListingSubmit);

/* [MCV-1536] The shared helper is reused by academics.machine and employment.machine
   with different target states and no $_VIEW of their own, so ON_CLOSE_MODAL is merged
   in here rather than added to createFacilityViewState. */
const facilityViewState = createFacilityViewState('$_NEW_EMPLOYER_FACILITY_VIEW', [
  'setFacility',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
]) as any;

export const facilityMachineCreator = (machineSetup: typeof msaasFacilitiesMachineSetup) =>
  machineSetup.createMachine({
    context: ({ spawn, input }) => ({
      facilityRef: spawn('facilityRef', {
        id:
          machineSetup === msaasFacilitiesMachineSetup
            ? 'facilityMsaasState'
            : 'facilityJobListingState',
        input: undefined,
      }),
      msaasJobListingFacilities: [],
      msaasJobListingFacilitiesSubmitted: false,
      facilitiesToDelete: [],
      states: [],
      modal: false,
      facility: undefined,
      employerFacility: undefined,
      loading: false,
      listingId: -1,
      parentRef: input?.parentRef,
    }),
    initial: '$_VIEW',
    on: {
      ON_LOAD: {
        actions: 'setListingId',
      },
      ON_ADD_NEW_FACILITY: {
        target: '.$_NEW_EMPLOYER_FACILITY_VIEW',
        actions: 'openModal',
      },
      /* [MCV-1536] ON_CLOSE_MODAL is intentionally NOT handled here. At the root it
         had no target, so dismissing the "Add New Employer Facility" dialog via X /
         Esc / backdrop hid the dialog but left the machine parked in
         $_NEW_EMPLOYER_FACILITY_VIEW — and ON_SUBMIT only exists in $_VIEW, so the
         wizard's Next/Save was silently dead for the rest of the step. It is now
         handled per-state with an explicit target back to $_VIEW.

         Keep it out of the root: a root handler would also fire during
         $_SEND_CREATE_NEW_FACILITY / $_SEND_SUBMIT and abort those in-flight
         invokes. */
    },
    states: {
      $_VIEW: {
        /* [MCV-1536] Invariant: when the step is at rest, the spawned facility actor is
           closed too. Unwinding only the parent left facilityRef in $_OPENED with its own
           modal flag still true, so reopening the outer dialog remounted
           EmployerFacilityForm with the nested "Add New Facility" dialog already open.

           On entry rather than on the ON_CLOSE_MODAL transition so every route back to
           rest is covered, including the invoke onDone/onError targets. Mirrors
           employerFacilities.machine.ts:225. ON_CLOSE is unhandled in the child's
           $_CLOSED state, so the initial entry and any repeat entry are no-ops. */
        entry: ({ context }) => {
          context.facilityRef.send({ type: 'ON_CLOSE' });
        },
        on: {
          ON_SUBMIT: {
            target: '$_SEND_SUBMIT',
            actions: [assign({ loading: true }), 'setSubmittedStepValues'],
          },
        },
      },
      $_NEW_EMPLOYER_FACILITY_VIEW: {
        on: {
          ...onFacilityFormOpen(),
          ON_NEW_FACILITY_SUBMIT: {
            target: '$_SEND_CREATE_NEW_FACILITY',
          },
          // [MCV-1536] Dismissing the dialog must return to $_VIEW, or ON_SUBMIT
          // (handled only there) is unreachable and the step can't be completed.
          ON_CLOSE_MODAL: {
            target: '$_VIEW',
            actions: 'closeModal',
          },
        },
      },
      $_SEND_CREATE_NEW_FACILITY: {
        invoke: {
          id: 'createEmployerFacility',
          src: 'createEmployerFacility',
          input: ({ context, event }: Scope) =>
            ({ context, event }) as { context: Context; event: OnAddFacilitiesEvent },
          onDone: {
            target: '$_VIEW',
            actions: ['closeModal', 'setEmployerFacility'],
          },
          onError: {
            target: '$_VIEW',
            actions: ['sendError', 'closeModal'],
          },
        },
      },
      $_SEND_SUBMIT: {
        entry: ['setLoading'],
        invoke: [
          {
            id: 'submit',
            src: 'submit',
            input: ({ context, event }) => {
              return { context, event } as { context: Context; event: OnSubmitEvent };
            },
            onDone: {
              target: '$_VIEW',
              actions: [
                'setFacilities',
                'sendUpdatedFacilityStepToParent',
                'resetLoading',
                'sendToNextPage',
              ],
            },
            onError: {
              target: '$_VIEW',
              actions: ['sendError', 'resetLoading'],
            },
          },
        ],
      },
      $_FACILITY_VIEW: {
        ...facilityViewState.$_FACILITY_VIEW,
        on: {
          ...facilityViewState.$_FACILITY_VIEW.on,
          // [MCV-1536] Closing the OUTER employer-facility dialog while the nested
          // "Add New Facility" form is open must unwind the whole stack to $_VIEW.
          // Without this the event is unhandled and the dialog never closes.
          ON_CLOSE_MODAL: {
            target: '$_VIEW',
            actions: 'closeModal',
          },
        },
      },
    },
  });

export const msaasFacilitiesMachine = facilityMachineCreator(msaasFacilitiesMachineSetup);
export const jobListingFacilitiesMachine = facilityMachineCreator(jobListingFacilitiesMachineSetup);
